import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Cap } from '@artmozaik/shared';
import { FIHRIST, konuAdi, trDuz } from '../kilavuz/fihrist';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ayarAdresi, ayarPaneliVar, gorunenAyarBolumleri, gorunenAyarKayitlari } from './AyarPaneli';
import { eslesme } from './aramaYardimci';
import Ikon, { type IkonAd } from './Ikon';
import { ANA_MENU, AYARLAR_OGE, IKINCIL_MENU, gorunenMenu } from './menu';

/**
 * ⌘K / Ctrl+K KOMUT ARAMASI — tek kutudan sayfa, ayar, kayıt ve yardım.
 *
 *  · Sayfalar : menüdeki her öğe, alt sayfalar ve sekmeler (`ui/menu.ts` + aşağıdaki ek liste)
 *  · Ayarlar  : `kilavuz/ayarDizini.ts`; sonuç ilgili bölüme gider ve alanı vurgular
 *  · Kayıtlar : ürün, cari, teklif; ekranların kendi liste uçları (`?ara=`) kullanılır, yeni uç yok
 *  · Yardım   : fihrist maddeleri
 *
 * Yetki: kullanıcının göremeyeceği hiçbir sayfa, ayar ya da madde sonuçlarda çıkmaz;
 * kayıt uçları zaten sunucuda korunur, yetkisi olmayan role istek bile atılmaz.
 */
type Sonuc = { grup: string; ad: string; nerede?: string; to: string; ikon: IkonAd };

/** Son gidilenler kullanıcıya özeldir: aynı tarayıcıda başka rolün gittiği sayfalar görünmez. */
const sonAnahtari = (kullaniciId: number | undefined) => `ccd-arama-son:${kullaniciId ?? 0}`;
const sonOku = (uid: number | undefined): Sonuc[] => { try { const v = JSON.parse(localStorage.getItem(sonAnahtari(uid)) ?? '[]'); return Array.isArray(v) ? v.slice(0, 5) : []; } catch { return []; } };
const sonYaz = (uid: number | undefined, s: Sonuc) => {
  try { localStorage.setItem(sonAnahtari(uid), JSON.stringify([{ ...s, grup: 'Son gidilenler' }, ...sonOku(uid).filter((x) => x.to !== s.to)].slice(0, 5))); } catch { /* özel pencere */ }
};

/** Menüde ayrı satırı olmayan sayfalar ve sekmeler. */
const EK_SAYFALAR: { ad: string; nerede: string; to: string; ikon: IkonAd; cap?: Cap; ek?: string[] }[] = [
  { ad: 'Fihrist', nerede: 'Yardım', to: '/fihrist', ikon: 'kitap', ek: ['sözlük', 'kavram', 'bu nedir', 'yardım'] },
  { ad: 'Kullanım kılavuzu', nerede: 'Yardım', to: '/kilavuz', ikon: 'bilgi', ek: ['kılavuz', 'yardım', 'nasıl kullanılır'] },
  { ad: 'Sunum', nerede: 'Yönetici sunumu', to: '/sunum', ikon: 'sunum', cap: 'settings.write', ek: ['ne yaptık', 'durum'] },
  { ad: 'Parola değiştir', nerede: 'Hesabım', to: '/parola', ikon: 'kilit', ek: ['şifre', 'sifre'] },
  { ad: 'Kasa hareketleri', nerede: 'Kasa ve Banka › Hareketler', to: '/kasa?sekme=hareketler', ikon: 'kasa', cap: 'cash.view', ek: ['giriş', 'çıkış', 'virman', 'yeni hareket'] },
  { ad: 'Emanetler', nerede: 'Kasa ve Banka › Emanetler', to: '/kasa?sekme=emanetler', ikon: 'kasa', cap: 'cash.view', ek: ['emanet', 'kimin parası kimde'] },
  { ad: 'Kasa ve banka tanımları', nerede: 'Kasa ve Banka › Tanımlar', to: '/kasa?sekme=tanimlar', ikon: 'kasa', cap: 'cash.view', ek: ['hesap', 'kategori', 'yeni hesap'] },
  { ad: 'Verilen çek ve senetler', nerede: 'Çek / Senet › Verilenler', to: '/cek-senet?sekme=verilenler', ikon: 'cek', cap: 'cheque.view', ek: ['çek', 'senet', 'ödenecek'] },
  { ad: 'Çek vade takvimi', nerede: 'Çek / Senet › Vade takvimi', to: '/cek-senet?sekme=takvim', ikon: 'takvim', cap: 'cheque.view', ek: ['çek', 'vade', 'takvim'] },
  { ad: 'Çek / senet geçmişi', nerede: 'Çek / Senet › Geçmiş', to: '/cek-senet?sekme=gecmis', ikon: 'cek', cap: 'cheque.view', ek: ['tahsil edilen', 'ödenen'] },
  { ad: 'Onay bekleyen hatırlatmalar', nerede: 'Borç Hatırlatma › Onay bekleyenler', to: '/tahsilat/hatirlatma?sekme=onay', ikon: 'zarf', cap: 'reminder.view', ek: ['kuyruk', 'onayla', 'sms', 'e-posta'] },
  { ad: 'Gönderilen hatırlatmalar', nerede: 'Borç Hatırlatma › Geçmiş', to: '/tahsilat/hatirlatma?sekme=gecmis', ikon: 'zarf', cap: 'reminder.view', ek: ['geçmiş', 'giden iletiler'] },
  { ad: 'Cari ciro ve hedef', nerede: 'Tanımlar ve Bakiyeler', to: '/finans?sekme=cari', ikon: 'grafik', cap: 'receivable.view', ek: ['hedef', 'sorumlu', 'ciro'] },
  { ad: 'Satış temsilcileri', nerede: 'Raporlar', to: '/rapor/temsilciler', ikon: 'grafik', cap: 'receivable.view', ek: ['temsilci', 'satışçı'] },
  { ad: 'Depo stok maliyeti', nerede: 'Raporlar › Stok ve Maliyet', to: '/rapor/stok', ikon: 'kutu', cap: 'receivable.view', ek: ['stok değeri', 'maliyet', 'envanter'] },
  { ad: 'Müşteri bakiyeleri', nerede: 'Tanımlar ve Bakiyeler', to: '/finans?sekme=musteri', ikon: 'lira', cap: 'receivable.view', ek: ['alacak', 'bakiye', 'ekstre'] },
  { ad: 'Tedarikçi bakiyeleri', nerede: 'Tanımlar ve Bakiyeler', to: '/finans?sekme=tedarikci', ikon: 'lira', cap: 'receivable.view', ek: ['borç', 'ödenmemiş alış', 'bakiye'] },
];

/** Boşken görünen sık kullanılanlar: menüde bulunan (yetkisi olan) ilk beşi gösterilir. */
const SIK_YOLLAR = ['/teklif', '/siparis', '/stok', '/crm', '/kasa', '/tahsilat', '/alis'];

/** Soru kalıbındaki dolgu kelimeler yardım aramasında sayılmaz ("ciro nasıl hesaplanır" → "ciro"). */
const DOLGU = new Set(['nasil', 'nedir', 'ne', 'hesaplanir', 'hesaplaniyor', 'yapilir', 'demek', 'mi', 'mu', 'bu', 'neden', 'ile']);

type Urun = { id: number; code: string; name: string };
type Cari = { id: number; code?: string | null; name: string; city?: string | null };
type Teklif = { id: number; no: string; cari: string; statusLabel: string };

export default function KomutArama({ kapat }: { kapat: () => void }) {
  const { can, me } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [secili, setSecili] = useState(0);
  const [kayitlar, setKayitlar] = useState<Sonuc[]>([]);
  const [araniyor, setAraniyor] = useState(false);
  const girdi = useRef<HTMLInputElement>(null);
  const kapatDugme = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const oncekiOdak = useRef<HTMLElement | null>(document.activeElement as HTMLElement | null);

  useEffect(() => {
    girdi.current?.focus();
    const once = oncekiOdak.current;
    return () => { once?.focus?.(); };
  }, []);

  /* ─── Sayfalar ve ayarlar: yerel, yetkiyle süzülmüş ─────────────────── */
  const sayfalar = useMemo<(Sonuc & { ek: string[] })[]>(() => {
    const s: (Sonuc & { ek: string[] })[] = [];
    for (const o of gorunenMenu([...ANA_MENU, ...IKINCIL_MENU, AYARLAR_OGE], can)) {
      s.push({ grup: 'Sayfalar', ad: o.label, to: o.hedef, ikon: o.ikon, ek: [] });
      for (const a of o.altlar) s.push({ grup: 'Sayfalar', ad: a.label, nerede: o.label, to: a.to, ikon: o.ikon, ek: [o.label] });
    }
    if (ayarPaneliVar(can)) for (const b of gorunenAyarBolumleri(can)) s.push({ grup: 'Sayfalar', ad: b.ad, nerede: 'Ayarlar', to: `/ayarlar/${b.id}`, ikon: b.ikon, ek: ['ayar', 'ayarlar'] });
    for (const e of EK_SAYFALAR) if (!e.cap || can(e.cap)) s.push({ grup: 'Sayfalar', ad: e.ad, nerede: e.nerede, to: e.to, ikon: e.ikon, ek: [e.nerede, ...(e.ek ?? [])] });
    return s;
  }, [can]);
  const ayarlar = useMemo(() => gorunenAyarKayitlari(can), [can]);
  const maddeler = useMemo(() => FIHRIST.filter((m) => !m.cap || can(m.cap)), [can]);

  const yerel = useMemo<Sonuc[]>(() => {
    if (!q.trim()) return [];
    const sirala = <T,>(dizi: T[], puan: (x: T) => number, azami: number) => dizi.map((x, i) => ({ x, i, p: puan(x) })).filter((y) => y.p >= 0)
      .sort((a, b) => a.p - b.p || a.i - b.i).slice(0, azami).map((y) => y.x);
    const sy = sirala(sayfalar, (x) => eslesme(q, x.ad, x.ek), 7).map(({ ek: _ek, ...r }) => r);
    const ay = sirala(ayarlar, (k) => eslesme(q, k.ad, [...k.anahtarKelimeler, k.nerede]), 8)
      .map((k): Sonuc => ({ grup: 'Ayarlar', ad: k.ad, nerede: k.nerede, to: ayarAdresi(k), ikon: 'ayar' }));
    const sade = trDuz(q).split(' ').filter((k) => k && !DOLGU.has(k)).join(' ') || q;
    const yr = sirala(maddeler, (m) => eslesme(sade, m.baslik, [...m.etiketler, m.kisa]), 6)
      .map((m): Sonuc => ({ grup: 'Yardım', ad: m.baslik, nerede: `Fihrist › ${konuAdi(m.konu)}`, to: `/fihrist#${m.id}`, ikon: 'kitap' }));
    return [...sy, ...ay, ...yr];
  }, [q, sayfalar, ayarlar, maddeler]);

  /* ─── Kayıtlar: ekranların kendi liste uçları ───────────────────────── */
  useEffect(() => {
    const metin = q.trim();
    if (metin.length < 2) { setKayitlar([]); setAraniyor(false); return; }
    let iptal = false;
    setAraniyor(true);
    const z = window.setTimeout(async () => {
      const e = encodeURIComponent(metin);
      const bos = <T,>(v: T) => () => v;
      const [u, c, t] = await Promise.all([
        can('stock.view') ? api<{ satirlar: Urun[] }>(`/api/urunler?ara=${e}&sirala=kod&limit=6`).then((r) => r.satirlar).catch(bos<Urun[]>([])) : [],
        can('quote.view') ? api<{ satirlar: Cari[] }>(`/api/crm/cariler?ara=${e}`).then((r) => r.satirlar).catch(bos<Cari[]>([])) : [],
        can('quote.view') ? api<{ satirlar: Teklif[] }>(`/api/teklif?ara=${e}`).then((r) => r.satirlar).catch(bos<Teklif[]>([])) : [],
      ]);
      if (iptal) return;
      setKayitlar([
        ...u.slice(0, 5).map((x): Sonuc => ({ grup: 'Ürünler', ad: x.name, nerede: x.code, to: `/stok/${x.id}`, ikon: 'kutu' })),
        ...c.slice(0, 5).map((x): Sonuc => ({ grup: 'Cariler', ad: x.name, nerede: [x.code, x.city].filter(Boolean).join(' · ') || undefined, to: `/crm/${x.id}`, ikon: 'kisi' })),
        ...t.slice(0, 5).map((x): Sonuc => ({ grup: 'Teklifler', ad: `${x.no} · ${x.cari}`, nerede: x.statusLabel, to: `/teklif/${x.id}`, ikon: 'belge' })),
      ]);
      setAraniyor(false);
    }, 260);
    return () => { iptal = true; window.clearTimeout(z); };
  }, [q, can]);

  const bos = !q.trim();
  const sonuclar = useMemo<Sonuc[]>(() => {
    if (!bos) {
      // Sıra: Sayfalar, Ayarlar, kayıtlar, Yardım.
      const y = yerel.filter((s) => s.grup === 'Yardım');
      return [...yerel.filter((s) => s.grup !== 'Yardım'), ...kayitlar, ...y];
    }
    const sik = SIK_YOLLAR.map((y) => sayfalar.find((s) => s.to === y && !s.nerede)).filter((s): s is NonNullable<typeof s> => Boolean(s)).slice(0, 5)
      .map(({ ek: _ek, ...r }): Sonuc => ({ ...r, grup: 'Sık kullanılanlar' }));
    return [...sonOku(me?.id), ...sik];
  }, [bos, yerel, kayitlar, sayfalar, me?.id]);

  useEffect(() => { setSecili(0); }, [q]);
  useEffect(() => { if (secili >= sonuclar.length) setSecili(Math.max(0, sonuclar.length - 1)); }, [sonuclar.length, secili]);
  useEffect(() => { liste.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [secili]);

  const git = (s: Sonuc) => { sonYaz(me?.id, s); kapat(); navigate(s.to); };

  const tus = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); kapat(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setSecili((i) => (sonuclar.length ? (i + 1) % sonuclar.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSecili((i) => (sonuclar.length ? (i - 1 + sonuclar.length) % sonuclar.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const s = sonuclar[secili]; if (s) git(s); }
    else if (e.key === 'Tab') {
      // Odak tuzağı: pencerede yalnız iki durak var (arama kutusu ve Kapat).
      e.preventDefault();
      (document.activeElement === girdi.current ? kapatDugme.current : girdi.current)?.focus();
    }
  };

  let oncekiGrup = '';
  return (
    <div className="ccd-komut-perde" onMouseDown={(e) => { if (e.target === e.currentTarget) kapat(); }}>
      <div className="ccd-komut" role="dialog" aria-modal="true" aria-label="Arama" onKeyDown={tus}>
        <div className="ccd-komut-ust">
          <Ikon ad="arama" boy={20} />
          <input ref={girdi} className="ccd-komut-girdi" type="search" inputMode="search" enterKeyHint="search" autoCapitalize="none" autoCorrect="off" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Sayfa, ayar, ürün, cari, teklif ara…" aria-label="Ara" autoComplete="off" spellCheck={false}
                 role="combobox" aria-expanded="true" aria-controls="ccd-komut-liste" aria-autocomplete="list"
                 aria-activedescendant={sonuclar[secili] ? `ccd-komut-${secili}` : undefined} />
          <button ref={kapatDugme} type="button" className="ccd-komut-kapat" onClick={kapat} aria-label="Aramayı kapat"><span className="ccd-komut-esc">Esc</span><Ikon ad="kapat" boy={18} /></button>
        </div>
        <div className="ccd-komut-liste" id="ccd-komut-liste" role="listbox" aria-label="Sonuçlar" ref={liste}>
          {sonuclar.length === 0 && (
            <div className="ccd-komut-bos">
              {bos ? 'Yazmaya başlayın: sayfa adı, ayar ("iban", "vade", "şablon"), ürün kodu, cari ya da teklif numarası.'
                : araniyor ? 'Aranıyor…' : `"${q.trim()}" için sonuç yok. Daha kısa ya da başka bir kelime deneyin.`}
            </div>
          )}
          {sonuclar.map((s, i) => {
            const baslik = s.grup !== oncekiGrup ? s.grup : null;
            oncekiGrup = s.grup;
            return (
              <div key={`${s.grup}-${s.to}-${i}`} role="presentation">
                {baslik && <div className="ccd-komut-grup" role="presentation">{baslik}</div>}
                <div id={`ccd-komut-${i}`} role="option" aria-selected={i === secili} className={`ccd-komut-oge${i === secili ? ' ccd-is-active' : ''}`}
                     onMouseMove={() => { if (i !== secili) setSecili(i); }} onClick={() => git(s)}>
                  <span className="ccd-komut-ikon"><Ikon ad={s.ikon} boy={18} /></span>
                  <span className="ccd-komut-ad">{s.ad}</span>
                  {s.nerede && <span className="ccd-komut-nerede">{s.nerede}</span>}
                  <span className="ccd-komut-gir" aria-hidden="true">↵</span>
                </div>
              </div>
            );
          })}
          {!bos && araniyor && sonuclar.length > 0 && <div className="ccd-komut-not">Kayıtlar aranıyor…</div>}
        </div>
        <div className="ccd-komut-alt" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> gez</span><span><kbd>↵</kbd> aç</span><span><kbd>Esc</kbd> kapat</span>
        </div>
      </div>
    </div>
  );
}
